import React, { useState } from 'react';
import { ArrowLeft, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useStore } from 'context/StoreContext';
import { useApi } from 'hooks/useApi';
import { ReportService } from 'services/reports/report.service';
import { Card } from 'components/common/Card';
import { Button } from 'components/common/Button';
import { Table, Column } from 'components/common/Table';
import { LoadingScreen, EmptyState } from 'components/common/Spinner';
import { Pagination } from 'components/common/Pagination';
import { formatCurrency, formatDate, formatPercent } from 'utils/formatters';

const PAGE_SIZE = 25;

type Format = 'text' | 'currency' | 'cost' | 'percent' | 'number' | 'date';
interface ColumnSpec { key: string; header: string; format?: Format; value?: (row: Record<string, any>) => any }

const productName = (row: Record<string, any>) => row.product_name ?? (Array.isArray(row.products) ? row.products[0]?.product_name : row.products?.product_name);

const reportDefinitions: Record<string, { title: string; endpoint: string; columns: ColumnSpec[] }> = {
  sales: { title: 'Daily sales', endpoint: 'sales', columns: [
    { key: 'business_date', header: 'Date', format: 'date' }, { key: 'net_sales', header: 'Net sales', format: 'currency' }, { key: 'units_sold', header: 'Units', format: 'number' },
  ] },
  profitability: { title: 'Item profitability', endpoint: 'profitability', columns: [
    { key: 'product_name', header: 'Product' }, { key: 'category', header: 'Category' }, { key: 'units_sold', header: 'Units', format: 'number' },
    { key: 'net_sales', header: 'Net sales', format: 'currency' }, { key: 'cost_of_goods', header: 'Cost of goods', format: 'currency' },
    { key: 'gross_profit', header: 'Gross profit', format: 'currency' }, { key: 'gross_margin', header: 'Margin', format: 'percent' },
    { key: 'cost_status', header: 'Cost', value: row => row.cost_status === 'VALID' ? 'Known' : 'Missing' },
  ] },
  'cost-changes': { title: 'Cost changes', endpoint: 'cost-changes', columns: [
    { key: 'effective_date', header: 'Effective', format: 'date' }, { key: 'product_name', header: 'Product' }, { key: 'supplier_name', header: 'Supplier' },
    { key: 'old_cost', header: 'Old cost', format: 'cost' }, { key: 'new_cost', header: 'New cost', format: 'cost' }, { key: 'change_pct', header: 'Change', format: 'percent' },
    { key: 'source', header: 'Source', value: row => ({ MANUAL: 'Manual', RECEIPT: 'Receipt', INVOICE: 'Invoice', SUPPLIER_CSV: 'Cost list', POS: 'POS' } as Record<string, string>)[row.source] ?? row.source },
  ] },
  'inventory-variance': { title: 'Inventory counts', endpoint: 'inventory-variance', columns: [
    { key: 'counted_at', header: 'Counted', format: 'date' }, { key: 'product_name', header: 'Product', value: productName },
    { key: 'expected_quantity', header: 'Expected', format: 'number' }, { key: 'physical_count', header: 'Counted', format: 'number' },
    { key: 'variance', header: 'Difference', format: 'number', value: row => Number(row.physical_count) - Number(row.expected_quantity) },
    { key: 'unit_cost', header: 'Unit cost', format: 'cost' },
  ] },
  'profit-leaks': { title: 'Profit leaks', endpoint: 'profit-leaks', columns: [
    { key: 'created_at', header: 'Found', format: 'date' }, { key: 'title', header: 'Issue' }, { key: 'recommended_action', header: 'What to do' },
    { key: 'estimated_monthly_impact', header: '$/month', format: 'currency' },
    { key: 'status', header: 'Status', value: row => ({ NEW: 'New', ACKNOWLEDGED: 'Seen', RESOLVED: 'Fixed', DISMISSED: 'Not relevant' } as Record<string, string>)[row.status] ?? row.status },
  ] },
};

function formatValue(value: any, format: Format = 'text'): React.ReactNode {
  if (value === null || value === undefined || value === '' || (typeof value === 'number' && !Number.isFinite(value))) return '—';
  switch (format) {
    case 'currency': return formatCurrency(Number(value), 2);
    case 'cost': return formatCurrency(Number(value), Number(value) < 10 ? 4 : 2);
    case 'percent': return formatPercent(Number(value));
    case 'number': return Number(value).toLocaleString();
    case 'date': return formatDate(String(value));
    default: return String(value);
  }
}

function ApiReportPage({ reportKey }: { reportKey: string }) {
  const definition = reportDefinitions[reportKey];
  const { org, currentStore } = useStore();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const { data: rows, loading, error } = useApi(
    () => ReportService.get(org?.id ?? '', currentStore?.id ?? '', definition.endpoint, page, PAGE_SIZE),
    [org?.id, currentStore?.id, definition.endpoint, page]
  );

  const [downloadError, setDownloadError] = useState('');
  const columns: Column<Record<string, any>>[] = definition.columns.map(spec => ({
    key: spec.key,
    header: spec.header,
    sortable: true,
    sortValue: row => spec.value ? spec.value(row) : row[spec.key],
    align: spec.format && spec.format !== 'text' && spec.format !== 'date' ? 'right' : 'left',
    render: row => <span className="text-sm text-gray-700">{formatValue(spec.value ? spec.value(row) : row[spec.key], spec.format)}</span>,
  }));

  const download = async () => {
    if (!org || !currentStore) return;
    setDownloadError('');
    try { await ReportService.download(org.id, currentStore.id, definition.endpoint); }
    catch (err: any) { setDownloadError(err?.message || 'The export failed. Please try again.'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/reports')} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900"><ArrowLeft size={14} /> Reports</button>
          <h1 className="text-xl font-bold text-gray-900">{definition.title}</h1>
        </div>
        <Button variant="secondary" size="sm" onClick={download}><Download size={13} /> Export CSV</Button>
      </div>
      {downloadError && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{downloadError}</div>}
      <Card padding={false}>
        {loading && !rows ? <LoadingScreen /> : error ? <div className="p-6 text-sm text-red-600">{error}</div> : !rows?.data.length ? (
          <EmptyState title="No report data yet" message="Import product and sales data to generate this report." />
        ) : <><Table columns={columns} data={rows.data} keyExtractor={row => String(row.id ?? row.product_id ?? row.business_date ?? JSON.stringify(row))} /><Pagination page={page} pageSize={PAGE_SIZE} total={rows.meta.total} onPageChange={setPage} /></>}
      </Card>
    </div>
  );
}

export const SalesReportPage = () => <ApiReportPage reportKey="sales" />;
export const ProfitabilityReportPage = () => <ApiReportPage reportKey="profitability" />;
export const CostChangesReportPage = () => <ApiReportPage reportKey="cost-changes" />;
export const InventoryVarianceReportPage = () => <ApiReportPage reportKey="inventory-variance" />;
export const ProfitLeaksReportPage = () => <ApiReportPage reportKey="profit-leaks" />;
export const SuppliersReportPage = () => <ApiReportPage reportKey="cost-changes" />;
